import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import legacyBlogs from "../data/blogs";
import PageSeo from "../components/PageSeo";
import "./BlogDetail.css";

function formatDate(value) {
  if (!value) return "Recently";
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  }
  return String(value);
}

function getReadTime(content = "", fallback = 1) {
  const wordCount = String(content || "")
    .replace(/[#*_`>\[\]\(\)\-|]/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
  return Math.max(1, Math.ceil(wordCount / 200)) || fallback;
}

function getInitials(name = "") {
  if (!name) return "MH";
  return (
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || "")
      .join("") || "MH"
  );
}

function buildSeoMeta(blog) {
  const rawTitle = String(blog?.title || "MotoHub India Bike Guide").replace(/\s+\|\s*MotoHub India$/i, "").trim();
  const rawDescription = String(blog?.description || blog?.excerpt || "MotoHub India helps riders compare bikes, calculate EMIs, and plan smarter ownership choices in India.")
    .replace(/\s+/g, " ")
    .trim();

  const normalizedTitle = rawTitle.length > 70 ? rawTitle : rawTitle;
  const seoTitle = normalizedTitle.includes("MotoHub India")
    ? normalizedTitle
    : `${normalizedTitle} | MotoHub India`;

  const seoDescription = rawDescription.length > 160
    ? `${rawDescription.slice(0, 157).trim()}...`
    : rawDescription;

  return {
    meta_title: seoTitle,
    meta_description: seoDescription,
  };
}

function normalizeLegacyBlog(blog) {
  const seoMeta = buildSeoMeta(blog);

  return {
    id: `legacy-${blog.slug}`,
    title: blog.title,
    slug: blog.slug,
    excerpt: blog.description || "",
    content: blog.content || "",
    cover_image: blog.image || null,
    category: "MotoHub Editorial",
    tags: [],
    read_time: getReadTime(blog.content || "", 2),
    views: 0,
    likes: 0,
    created_at: blog.date || null,
    published_at: blog.date || null,
    author: {
      id: `legacy-author-${blog.slug}`,
      name: blog.author || "MotoHub India Editorial",
      username: blog.slug,
      avatar_url: null,
      city: "India",
      bike: "",
    },
    faqs: blog.faqs || [],
    meta_title: seoMeta.meta_title,
    meta_description: seoMeta.meta_description,
    source: "legacy",
  };
}

export default function BlogDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  const legacyPost = useMemo(() => {
    const match = legacyBlogs.find((blog) => blog.slug === slug);
    return match ? normalizeLegacyBlog(match) : null;
  }, [slug]);

  useEffect(() => {
    let active = true;

    const fetchWithAuthorRelation = async () =>
      supabase
        .from("blog_posts")
        .select("*, author:profiles(name, username, avatar_url, city, bike, riding_style)")
        .eq("slug", slug)
        .eq("status", "approved")
        .maybeSingle();

    const fetchWithoutAuthorRelation = async () =>
      supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug)
        .eq("status", "approved")
        .maybeSingle();

    const loadPost = async () => {
      setLoading(true);

      let response = await fetchWithAuthorRelation();
      if (response.error) response = await fetchWithoutAuthorRelation();

      if (!active) return;

      if (response.data) {
        const data = response.data;
        setPost({
          ...data,
          cover_image: data.cover_image || null,
          category: data.category || "General",
          read_time: Number(data.read_time || 1),
          views: Number(data.views || 0),
          likes: Number(data.likes || 0),
          published_at: data.published_at || data.created_at || null,
          created_at: data.created_at || data.published_at || null,
          author:
            data.author ||
            (data.author_id
              ? {
                  id: data.author_id,
                  name: "MotoHub Rider",
                  username: "",
                  avatar_url: null,
                  city: "India",
                  bike: "",
                }
              : {
                  id: "",
                  name: "MotoHub Rider",
                  username: "",
                  avatar_url: null,
                  city: "India",
                  bike: "",
                }),
          source: "community",
        });

        if (Number(data.views || 0) >= 0) {
          supabase.from("blog_posts").update({ views: Number(data.views || 0) + 1 }).eq("id", data.id).catch(() => {});
        }
        setLoading(false);
        return;
      }

      setPost(legacyPost);
      setLoading(false);
    };

    loadPost();
    return () => {
      active = false;
    };
  }, [slug, legacyPost]);

  if (loading) {
    return (
      <div className="blogdetail-page">
        <div className="blogdetail-shell">
          <div className="blogdetail-state">Loading blog...</div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="blogdetail-page">
        <div className="blogdetail-shell">
          <PageSeo
            title="Blog Not Found | MotoHub India"
            description="The requested blog post could not be found."
            path={`/blog/${slug}`}
            noindex
          />
          <div className="blogdetail-state">
            <h1>Blog not found</h1>
            <p>The blog post you are looking for is not available.</p>
            <button type="button" onClick={() => navigate("/blogs")} className="blogdetail-primary-btn">
              Back to Blogs
            </button>
          </div>
        </div>
      </div>
    );
  }

  const description =
    post.excerpt ||
    (typeof post.content === "string" ? post.content.slice(0, 160) : "") ||
    "Read the latest MotoHub India blog post.";

  const seoTitle = post.meta_title || `${post.title} | MotoHub India`;
  const seoDescription = post.meta_description || description;
  const pageKeywords = Array.isArray(post.keywords) && post.keywords.length > 0
    ? post.keywords
    : [post.title, post.category || "MotoHub India", "MotoHub India blogs", "bike blogs India", "bike buying guide India", "bike comparison India"];

  const authorName = post.author?.name || "MotoHub India Editorial";
  const authorUsername = post.author?.username || "";
  const authorLink = authorUsername ? `/riders/${authorUsername}` : "/riders";

  return (
    <>
      <PageSeo
        title={seoTitle}
        description={seoDescription}
        keywords={pageKeywords}
        path={`/blog/${slug}`}
        image={post.cover_image || "/og-image.jpg"}
        ogImageAlt={post.title}
        author={authorName}
      />

      <div className="blogdetail-page">
        <section className="blogdetail-hero">
          <div className="blogdetail-shell">
            <button type="button" onClick={() => navigate("/blogs")} className="blogdetail-back-btn">
              Back to Blogs
            </button>
            <div className="blogdetail-meta-row">
              <span className="blogdetail-chip">{post.category || "General"}</span>
              <span>{post.read_time || 1} min read</span>
              <span>{formatDate(post.published_at || post.created_at)}</span>
              <span>{Number(post.views || 0).toLocaleString("en-IN")} views</span>
            </div>
            <h1>{post.title}</h1>
            <p>{description}</p>
          </div>
        </section>

        <main className="blogdetail-shell blogdetail-layout">
          <article className="blogdetail-main-card">
            {post.cover_image ? (
              <img src={post.cover_image} alt={post.title} className="blogdetail-cover" />
            ) : (
              <div className="blogdetail-cover-placeholder">MotoHub India</div>
            )}

            <div className="blogdetail-content-wrap">
              <div className="blogdetail-author-card">
                <div className="blogdetail-author-left">
                  {post.author?.avatar_url ? (
                    <img src={post.author.avatar_url} alt={authorName} className="blogdetail-author-avatar" />
                  ) : (
                    <div className="blogdetail-author-avatar blogdetail-author-fallback">{getInitials(authorName)}</div>
                  )}
                  <div>
                    <p className="blogdetail-author-name">
                      {authorName}
                      {authorUsername ? ` (@${authorUsername})` : ""}
                    </p>
                    <p className="blogdetail-author-sub">
                      {post.author?.city || "India"}
                      {post.author?.bike ? ` • ${post.author.bike}` : ""}
                    </p>
                  </div>
                </div>
                <button type="button" onClick={() => navigate(authorLink)} className="blogdetail-secondary-btn">
                  View Rider
                </button>
              </div>

              <div className="blogdetail-content">{post.content || "No content available."}</div>

              {Array.isArray(post.faqs) && post.faqs.length > 0 ? (
                <div className="blogdetail-faqs">
                  <h2>FAQs</h2>
                  <div>
                    {post.faqs.map((faq, index) => (
                      <article key={`${faq.q || "faq"}-${index}`} className="blogdetail-faq-item">
                        <h3>{faq.q}</h3>
                        <p>{faq.a}</p>
                      </article>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </article>

          <aside className="blogdetail-side">
            <div className="blogdetail-side-card">
              <p className="blogdetail-side-title">Article Actions</p>
              <button
                type="button"
                className="blogdetail-secondary-btn full"
                onClick={() => {
                  const url = `https://wa.me/?text=${encodeURIComponent(`${post.title} - https://motohubindia.com/blog/${slug}`)}`;
                  window.open(url, "_blank", "noopener,noreferrer");
                }}
              >
                Share on WhatsApp
              </button>
              <Link to="/blogs" className="blogdetail-primary-link">
                Explore More Blogs
              </Link>
            </div>

            <div className="blogdetail-side-card">
              <p className="blogdetail-side-title">Quick Details</p>
              <div className="blogdetail-quick-list">
                <p><span>Read Time</span><strong>{post.read_time || 1} min</strong></p>
                <p><span>Views</span><strong>{Number(post.views || 0).toLocaleString("en-IN")}</strong></p>
                <p><span>Published</span><strong>{formatDate(post.published_at || post.created_at)}</strong></p>
              </div>
            </div>
          </aside>
        </main>
      </div>
    </>
  );
}
